import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router';

import { useUser } from '@clerk/react';
import log from 'loglevel';

import { URL_PARAMS } from '../../hooks';
import { useDeleteTaskGroup as useDeleteTaskGroupService } from '../../services/taskGroups.service';
import { useInboxEntriesStore } from '../../store';
import { useDoBoxFilterSortStore } from '../../store/DoBox';

interface UseDeleteTaskGroupReturn {
  handleDeleteTaskGroup: (taskGroupId: string) => Promise<boolean>;
  isDeleting: boolean;
  deleteError: Error | null;
}

export function useDeleteTaskGroup(): UseDeleteTaskGroupReturn {
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<Error | null>(null);
  const { user } = useUser();
  const [, setSearchParams] = useSearchParams();

  const defaultTaskGroupId =
    (user?.unsafeMetadata?.defaultTaskGroupId as string) ?? null;

  const { deleteTaskGroup: deleteTaskGroupService } =
    useDeleteTaskGroupService();

  const handleDeleteTaskGroup = useCallback(
    async (taskGroupId: string): Promise<boolean> => {
      setIsDeleting(true);
      setDeleteError(null);

      try {
        const success = await deleteTaskGroupService(taskGroupId);

        if (!success) return false;

        const state = useInboxEntriesStore.getState();

        const {
          taskManager: {
            activeTaskGroup,
            taskGroups: { removeTaskGroup },
          },
        } = state;

        const isDeletingActiveGroup =
          activeTaskGroup.taskGroupId === taskGroupId;

        if (isDeletingActiveGroup && defaultTaskGroupId) {
          useDoBoxFilterSortStore
            .getState()
            .switchTaskGroupAndRestore(defaultTaskGroupId);
          activeTaskGroup.setActiveTaskGroup(defaultTaskGroupId);
        }

        removeTaskGroup(taskGroupId);
        useDoBoxFilterSortStore.getState().removeTaskGroupSnapshot(taskGroupId);

        if (isDeletingActiveGroup) {
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.delete(URL_PARAMS.TASK_GROUP_ID);
              return next;
            },
            { replace: true },
          );
        }

        return true;
      } catch (error) {
        const errorInstance =
          error instanceof Error
            ? error
            : new Error('Failed to delete task group');

        setDeleteError(errorInstance);
        log.error('Error deleting task group:', error);

        return false;
      } finally {
        setIsDeleting(false);
      }
    },
    [deleteTaskGroupService, defaultTaskGroupId, setSearchParams],
  );

  return {
    handleDeleteTaskGroup,
    isDeleting,
    deleteError,
  };
}
